import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Alert, AlertDescription } from './ui/alert'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { AppIcon } from './ui/icon'
import { mdiCodeJson, mdiCheck, mdiClose, mdiAlertCircle, mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { z } from 'zod'
import { type PropertyDefinition } from '../hooks/useQueries'

interface JsonPropertyDisplayProps {
    value: any
    propertyDefinition?: PropertyDefinition
    onChange?: (value: any) => void
    readonly?: boolean
    className?: string
}

interface ValidationError {
    line: number
    message: string
    path?: string
}

export function JsonPropertyDisplay({
    value,
    propertyDefinition,
    onChange,
    readonly = false,
    className = ''
}: JsonPropertyDisplayProps) {
    const [isEditorOpen, setIsEditorOpen] = useState(false)
    const [editorValue, setEditorValue] = useState('')
    const [isValidating, setIsValidating] = useState(false)
    const [lastValidJson, setLastValidJson] = useState<any>(null)
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
    const [showSchema, setShowSchema] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const lineNumbersRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const isClosingRef = useRef(false)

    // Get JSON schema from property definition if available
    const jsonSchema = propertyDefinition?.properties?.json_schema as any
    let parsedSchema: any = null

    // Try to parse the JSON schema
    if (jsonSchema && typeof jsonSchema === 'string') {
        try {
            parsedSchema = JSON.parse(jsonSchema)
        } catch {
            parsedSchema = { error: 'Invalid schema format' }
        }
    } else if (jsonSchema && typeof jsonSchema === 'object') {
        parsedSchema = jsonSchema
    }

    // Convert JSON Schema type to Zod type (simplified)
    const convertJsonSchemaToZod = useCallback((schema: any): z.ZodTypeAny => {
        if (!schema || typeof schema !== 'object') return z.any()

        switch (schema.type) {
            case 'string':
                if (schema.pattern) {
                    return z.string().regex(new RegExp(schema.pattern))
                }
                if (schema.minLength !== undefined || schema.maxLength !== undefined) {
                    let chain = z.string()
                    if (schema.minLength !== undefined) chain = chain.min(schema.minLength)
                    if (schema.maxLength !== undefined) chain = chain.max(schema.maxLength)
                    return chain
                }
                return z.string()
            case 'number': {
                let numChain = z.number()
                if (schema.minimum !== undefined) numChain = numChain.min(schema.minimum)
                if (schema.maximum !== undefined) numChain = numChain.max(schema.maximum)
                return numChain
            }
            case 'integer': {
                let intChain = z.number().int()
                if (schema.minimum !== undefined) intChain = intChain.min(schema.minimum)
                if (schema.maximum !== undefined) intChain = intChain.max(schema.maximum)
                return intChain
            }
            case 'boolean':
                return z.boolean()
            case 'array':
                return z.array(convertJsonSchemaToZod(schema.items || {}))
            case 'object':
                if (schema.properties) {
                    const shape: Record<string, z.ZodTypeAny> = {}
                    for (const [key, prop] of Object.entries(schema.properties)) {
                        shape[key] = convertJsonSchemaToZod(prop)
                    }
                    if (schema.required && Array.isArray(schema.required)) {
                        return z.object(shape).required(schema.required as any)
                    }
                    return z.object(shape).partial()
                }
                return z.record(z.string(), z.any())
            default:
                return z.any()
        }
    }, [])

    // Validate JSON against schema with line tracking
    const validateJson = useCallback((jsonString: string): { isValid: boolean; parsedValue?: any; errors?: ValidationError[] } => {
        const errors: ValidationError[] = []

        try {
            const parsedValue = JSON.parse(jsonString)

            // If we have a schema, validate against it
            if (parsedSchema && typeof parsedSchema === 'object' && !parsedSchema.error) {
                try {
                    // Handle oneOf validation (for ISO 20022 address)
                    if (parsedSchema.oneOf && Array.isArray(parsedSchema.oneOf)) {
                        let oneOfValid = false

                        for (const schemaOption of parsedSchema.oneOf) {
                            try {
                                const shape: Record<string, z.ZodTypeAny> = {}
                                if (schemaOption.properties) {
                                    for (const [key, prop] of Object.entries(schemaOption.properties)) {
                                        shape[key] = convertJsonSchemaToZod(prop)
                                    }
                                }
                                const tempSchema = z.object(shape).partial()
                                tempSchema.parse(parsedValue)
                                oneOfValid = true
                                break
                            } catch {
                                // Continue trying other schemas
                            }
                        }

                        if (!oneOfValid) {
                            errors.push({
                                line: 1,
                                message: 'Address must match either structured format (with streetName, townName, etc.) or unstructured format (with addressLine array)',
                                path: 'root'
                            })
                        }
                    } else {
                        // Regular schema validation
                        const shape: Record<string, z.ZodTypeAny> = {}
                        if (parsedSchema.properties) {
                            for (const [key, prop] of Object.entries(parsedSchema.properties)) {
                                shape[key] = convertJsonSchemaToZod(prop)
                            }
                        }

                        let zodSchema: z.ZodTypeAny = z.any()
                        if (parsedSchema.type === 'object') {
                            if (parsedSchema.required && Array.isArray(parsedSchema.required)) {
                                zodSchema = z.object(shape).required(parsedSchema.required as any)
                            } else {
                                zodSchema = z.object(shape).partial()
                            }
                        } else if (parsedSchema.type) {
                            zodSchema = convertJsonSchemaToZod(parsedSchema)
                        }

                        zodSchema.parse(parsedValue)
                    }

                    // Additional custom validations for ISO 20022
                    if (parsedSchema.title?.includes('ISO20022') || parsedSchema.$id?.includes('iso20022')) {
                        // Check country format
                        if (parsedValue.country && !/^[A-Z]{2}$/.test(parsedValue.country)) {
                            errors.push({
                                line: findLineNumber(jsonString, 'country'),
                                message: 'Country must be a valid ISO 3166-1 alpha-2 code (e.g., US, GB, FR)',
                                path: 'country'
                            })
                        }

                        // Check addressType code if present
                        if (parsedValue.addressType?.code && !['ADDR', 'PBOX', 'HOME', 'BIZZ', 'MLTO', 'DLVY'].includes(parsedValue.addressType.code)) {
                            errors.push({
                                line: findLineNumber(jsonString, 'addressType.code'),
                                message: 'Address type code must be one of: ADDR, PBOX, HOME, BIZZ, MLTO, DLVY',
                                path: 'addressType.code'
                            })
                        }

                        // Check string lengths
                        const maxLengths: Record<string, number> = {
                            streetName: 70,
                            buildingNumber: 16,
                            buildingName: 35,
                            floor: 70,
                            postBox: 16,
                            room: 70,
                            postCode: 16,
                            townName: 35,
                            townLocationName: 35,
                            districtName: 35,
                            countrySubDivision: 35
                        }

                        for (const [field, maxLength] of Object.entries(maxLengths)) {
                            if (parsedValue[field] && typeof parsedValue[field] === 'string' && parsedValue[field].length > maxLength) {
                                errors.push({
                                    line: findLineNumber(jsonString, field),
                                    message: `${field} exceeds maximum length of ${maxLength} characters`,
                                    path: field
                                })
                            }
                        }

                        // Check addressLine array
                        if (parsedValue.addressLine) {
                            if (!Array.isArray(parsedValue.addressLine)) {
                                errors.push({
                                    line: findLineNumber(jsonString, 'addressLine'),
                                    message: 'addressLine must be an array',
                                    path: 'addressLine'
                                })
                            } else if (parsedValue.addressLine.length > 7) {
                                errors.push({
                                    line: findLineNumber(jsonString, 'addressLine'),
                                    message: 'addressLine can have at most 7 elements',
                                    path: 'addressLine'
                                })
                            } else {
                                parsedValue.addressLine.forEach((line: any, index: number) => {
                                    if (typeof line !== 'string') {
                                        errors.push({
                                            line: findLineNumber(jsonString, `addressLine[${index}]`),
                                            message: `addressLine[${index}] must be a string`,
                                            path: `addressLine[${index}]`
                                        })
                                    } else if (line.length > 70) {
                                        errors.push({
                                            line: findLineNumber(jsonString, `addressLine[${index}]`),
                                            message: `addressLine[${index}] exceeds maximum length of 70 characters`,
                                            path: `addressLine[${index}]`
                                        })
                                    }
                                })
                            }
                        }
                    }

                } catch (schemaError) {
                    if (schemaError instanceof z.ZodError) {
                        schemaError.issues.forEach((err: any) => {
                            const path = Array.isArray(err.path) ? err.path.join('.') : 'unknown'
                            errors.push({
                                line: findLineNumber(jsonString, path),
                                message: err.message,
                                path
                            })
                        })
                    } else {
                        errors.push({
                            line: 1,
                            message: schemaError instanceof Error ? schemaError.message : 'Unknown schema validation error',
                            path: 'root'
                        })
                    }
                }
            }

            return {
                isValid: errors.length === 0,
                parsedValue,
                errors: errors.length > 0 ? errors : undefined
            }
        } catch (parseError) {
            const errorMsg = parseError instanceof Error ? parseError.message : 'Invalid JSON'
            // Try to extract line number from JSON parse error
            const lineMatch = errorMsg.match(/line (\d+)/i)
            const lineNumber = lineMatch ? parseInt(lineMatch[1]) : 1

            return {
                isValid: false,
                errors: [{
                    line: lineNumber,
                    message: errorMsg,
                    path: 'json'
                }]
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedSchema]) // Remove convertJsonSchemaToZod to prevent infinite loop

    // Find line number for a JSON path
    const findLineNumber = useCallback((jsonString: string, path: string): number => {
        const lines = jsonString.split('\n')

        // Special handling for array indices
        if (path.includes('[') && path.includes(']')) {
            const match = path.match(/^(.+)\[(\d+)\]$/)
            if (match) {
                const arrayPath = match[1]
                parseInt(match[2])
                for (let i = 0; i < lines.length; i++) {
                    if (lines[i].includes(`"${arrayPath}"`)) {
                        return i + 1
                    }
                }
            }
        }

        // Regular property lookup
        const parts = path.split('.')
        const lastName = parts[parts.length - 1]

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim()
            // Check for quoted property name
            if (line.includes(`"${lastName}"`) || line.includes(`'${lastName}'`)) {
                return i + 1
            }
        }

        return 1 // Default to first line if not found
    }, [])

    // Sync scroll between textarea and line numbers
    const syncScroll = useCallback(() => {
        if (textareaRef.current && lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop
        }
    }, [])

    // Debounced validation effect
    useEffect(() => {
        if (!editorValue.trim() || readonly) {
            setValidationErrors([])
            return
        }

        const timeoutId = setTimeout(() => {
            setIsValidating(true)
            const validation = validateJson(editorValue)

            if (validation.isValid) {
                setValidationErrors([])
                setLastValidJson(validation.parsedValue)
            } else {
                setValidationErrors(validation.errors || [])
            }
            setIsValidating(false)
        }, 300) // 300ms debounce

        return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorValue, readonly]) // Remove validateJson from dependencies to prevent infinite loop

    const handleSaveJson = useCallback(() => {
        const validation = validateJson(editorValue)

        if (!validation.isValid) {
            setValidationErrors(validation.errors || [])
            return
        }

        setValidationErrors([])

        // Call onChange before closing the dialog
        if (onChange && validation.parsedValue !== undefined) {
            onChange(validation.parsedValue)
        }

        // Set closing flag to prevent onOpenChange recursion
        isClosingRef.current = true
        setIsEditorOpen(false)

        // Reset flag after state update
        setTimeout(() => {
            isClosingRef.current = false
        }, 100)
    }, [editorValue, validateJson, onChange])

    const handleCancelEdit = useCallback(() => {
        // If we're already closing, don't do anything
        if (isClosingRef.current) return

        // Set closing flag to prevent onOpenChange recursion
        isClosingRef.current = true
        setIsEditorOpen(false)

        // Reset state after a small delay
        setTimeout(() => {
            setEditorValue('')
            setValidationErrors([])
            setLastValidJson(null)
            setShowSchema(false)
            isClosingRef.current = false
        }, 0)
    }, [])

    const handleEditorChange = useCallback((newValue: string) => {
        setEditorValue(newValue)
        // Sync scroll after content change
        setTimeout(syncScroll, 0)
    }, [syncScroll])

    // Format JSON on demand
    const formatJson = useCallback(() => {
        try {
            const parsed = JSON.parse(editorValue)
            const formatted = JSON.stringify(parsed, null, 2)
            setEditorValue(formatted)
            setValidationErrors([])
        } catch {
            setValidationErrors([{
                line: 1,
                message: 'Cannot format invalid JSON',
                path: 'format'
            }])
        }
    }, [editorValue])

    // Insert ISO 20022 template
    const insertTemplate = useCallback(() => {
        const template = {
            addressType: {
                code: "ADDR"
            },
            streetName: "Main Street",
            buildingNumber: "123",
            buildingName: "Tower A",
            floor: "5",
            room: "502",
            postCode: "12345",
            townName: "New York",
            countrySubDivision: "NY",
            country: "US"
        }

        const formatted = JSON.stringify(template, null, 2)
        setEditorValue(formatted)
        setValidationErrors([])
        setLastValidJson(template)
    }, [])

    // Calculate error lines for highlighting
    const errorLineNumbers = useMemo(() => {
        return new Set(validationErrors.map(err => err.line))
    }, [validationErrors])

    // Generate line numbers content
    const lineNumbersContent = useMemo(() => {
        const lines = editorValue.split('\n')
        const elements = []

        for (let i = 0; i < Math.max(lines.length, 1); i++) {
            const lineNumber = i + 1
            const hasError = errorLineNumbers.has(lineNumber)
            elements.push(
                <div
                    key={`line-${lineNumber}`}
                    className={`px-3 text-right ${
                        hasError
                            ? 'text-destructive font-semibold bg-destructive/10'
                            : 'text-muted-foreground'
                    }`}
                    style={{ lineHeight: '1.5rem' }}
                >
                    {lineNumber}
                </div>
            )
        }

        return elements
    }, [editorValue, errorLineNumbers])

    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <Dialog open={isEditorOpen} onOpenChange={(open) => {
            if (!open && !isClosingRef.current) {
                handleCancelEdit()
            }
        }}>
                <DialogTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="font-mono text-xs bg-muted/50 px-2 py-1 h-auto hover:bg-muted/70 transition-colors text-muted-foreground hover:text-foreground p-0"
                        title={readonly ? "View JSON" : "Edit JSON"}
                        onClick={(event) => {
                            event.stopPropagation()
                            if (!readonly) {
                                const jsonString = JSON.stringify(value, null, 2)
                                setEditorValue(jsonString)
                                setValidationErrors([])
                                setShowSchema(false)
                                setIsEditorOpen(true)
                            }
                        }}
                    >
                        {'{...}'}
                    </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => {
                    e.preventDefault()
                    handleCancelEdit()
                }}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AppIcon path={mdiCodeJson} size="md" />
                            {readonly ? 'View JSON Property' : 'Edit JSON Property'}
                            {propertyDefinition && (
                                <span className="text-sm font-normal text-muted-foreground">
                                    - {typeof propertyDefinition.name === 'object'
                                        ? (propertyDefinition.name as any).en || (propertyDefinition.name as any).fr || Object.values(propertyDefinition.name)[0]
                                        : propertyDefinition.name || propertyDefinition.code || 'JSON'
                                    }
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Editor Container */}
                        <div
                            ref={containerRef}
                            className="relative border rounded-md overflow-hidden"
                        >
                            {/* Line Numbers */}
                            <div
                                ref={lineNumbersRef}
                                className="absolute left-0 top-0 bottom-0 w-16 bg-muted/30 border-r border-border overflow-y-auto"
                                style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
                            >
                                <div className="font-mono text-xs select-none">
                                    {lineNumbersContent}
                                </div>
                            </div>

                            {/* Textarea */}
                            <textarea
                                ref={textareaRef}
                                value={editorValue}
                                onChange={(e) => handleEditorChange(e.target.value)}
                                onScroll={syncScroll}
                                placeholder="Enter valid ISO 20022 address JSON..."
                                className={`w-full min-h-[400px] px-16 py-3 font-mono text-sm resize-none focus:outline-none bg-background ${
                                    validationErrors.length > 0 ? 'border-destructive' :
                                    lastValidJson ? 'border-green-500' :
                                    'border-input'
                                } ${readonly ? 'bg-muted/50 cursor-not-allowed' : ''}`}
                                style={{
                                    lineHeight: '1.5rem',
                                    tabSize: 2,
                                    MozTabSize: 2,
                                    WebkitTabSize: 2,
                                    OTabSize: 2
                                } as React.CSSProperties}
                                spellCheck={false}
                                readOnly={readonly}
                            />

                            {/* Validation status indicator */}
                            {!readonly && editorValue.trim() && (
                                <div className="absolute top-3 right-3">
                                    {isValidating ? (
                                        <div className="motion-safe:animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                                    ) : validationErrors.length > 0 ? (
                                        <AppIcon size="sm" path={mdiAlertCircle} className="h-5 w-5 text-destructive" />
                                    ) : lastValidJson ? (
                                        <AppIcon size="sm" path={mdiCheck} className="h-5 w-5 text-green-500" />
                                    ) : null}
                                </div>
                            )}
                        </div>

                        {/* Validation errors with line numbers */}
                        {validationErrors.length > 0 && (
                            <Alert className="border-destructive">
                                <AppIcon size="sm" path={mdiAlertCircle} className="h-4 w-4" />
                                <AlertDescription className="text-destructive">
                                    <div className="space-y-2">
                                        <p className="font-medium">Validation Errors:</p>
                                        <div className="space-y-1">
                                            {validationErrors.map((error, index) => (
                                                <div key={index} className="flex items-start gap-2 text-sm">
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-destructive/20 text-destructive text-xs font-semibold mt-0.5 flex-shrink-0">
                                                        {error.line}
                                                    </span>
                                                    <span>{error.message}</span>
                                                    {error.path && (
                                                        <span className="text-muted-foreground text-xs flex-shrink-0">
                                                            (path: {error.path})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Success message */}
                        {!isValidating && lastValidJson && validationErrors.length === 0 && editorValue.trim() && (
                            <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
                                <AppIcon size="sm" path={mdiCheck} className="h-4 w-4 text-green-600" />
                                <AlertDescription className="text-green-700 dark:text-green-300">
                                    Valid ISO 20022 address format
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center">
                            <div className="flex gap-2">
                                {(parsedSchema?.title?.includes('ISO20022') || parsedSchema?.$id?.includes('iso20022')) && (
                                    <Button
                                        variant="outline"
                                        onClick={insertTemplate}
                                        className="text-sm"
                                        disabled={readonly}
                                    >
                                        Insert Template
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={formatJson}
                                    className="text-sm"
                                    disabled={readonly}
                                >
                                    Format JSON
                                </Button>
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={handleCancelEdit}
                                    className="text-sm"
                                >
                                    <AppIcon path={mdiClose} size="sm" className="mr-1" />
                                    {readonly ? 'Close' : 'Cancel'}
                                </Button>
                                {!readonly && (
                                    <Button
                                        onClick={handleSaveJson}
                                        className="text-sm"
                                        disabled={!editorValue.trim() || validationErrors.length > 0 || isValidating}
                                    >
                                        <AppIcon path={mdiCheck} size="sm" className="mr-1" />
                                        Save {validationErrors.length > 0 && '(Fix Errors)'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Collapsible Schema Viewer at the bottom */}
                        {parsedSchema && (
                            <Collapsible open={showSchema} onOpenChange={setShowSchema}>
                                <CollapsibleTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start"
                                        data-testid="toggle-schema-view"
                                    >
                                        <AppIcon
                                            path={showSchema ? mdiChevronUp : mdiChevronDown}
                                            size="sm"
                                            className="mr-2"
                                        />
                                        View JSON Schema
                                    </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="mt-2">
                                    <div className="bg-muted/50 rounded-md p-4 max-h-60 overflow-y-auto">
                                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                                            {JSON.stringify(parsedSchema, null, 2)}
                                        </pre>
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}